import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RfShell, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { useScope } from "@/hooks/use-scope";
import { useAuth } from "@/hooks/use-auth";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { ChevronRight, AlertTriangle, PlayCircle } from "lucide-react";
import { listOpenPickJobs, startJobSession, type PickJobRow } from "@/lib/pick-jobs.functions";
import { runServerFn } from "@/lib/server-fn";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/app/rf/pick")({
  component: RfPickQueue,
});

function RfPickQueue() {
  const { tenantId, facilityId } = useScope();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const listJobsFn = useServerFn(listOpenPickJobs);
  const startJobFn = useServerFn(startJobSession);

  const { data: jobs, isLoading, error } = useQuery({
    queryKey: ["open-pick-jobs", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listJobsFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  // Fetch assignee_id for the listed jobs so we can split assigned-to-me vs available.
  const allJobs: PickJobRow[] = jobs
    ? [...jobs.bulk_batch, ...jobs.pick_to_bin, ...jobs.order_by_order]
    : [];
  const jobIds = allJobs.map((j) => j.id);
  const { data: assignments = {} } = useQuery({
    queryKey: ["pick-job-assignments", tenantId, facilityId, jobIds.join(",")],
    enabled: !!tenantId && jobIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pick_jobs")
        .select("id, assigned_to")
        .in("id", jobIds);
      if (error) throw new Error(error.message);
      const map: Record<string, string | null> = {};
      for (const r of (data ?? []) as Array<{ id: string; assigned_to: string | null }>) {
        map[r.id] = r.assigned_to;
      }
      return map;
    },
  });

  const startJob = async (job: PickJobRow) => {
    try {
      const res = await runServerFn(() => startJobFn({ data: { jobId: job.id } }), "Starting job");
      qc.invalidateQueries({ queryKey: ["open-pick-jobs"] });
      navigate({ to: "/app/pick/session/$sessionId", params: { sessionId: res.sessionId } });
    } catch {
      // toast handled by runServerFn
    }
  };

  if (!tenantId || !facilityId) {
    return (
      <RfShell title="Picking" back>
        <RfScopeMissing />
      </RfShell>
    );
  }

  const mine = allJobs.filter((j) => assignments[j.id] && assignments[j.id] === user?.id);
  const others = allJobs.filter((j) => !assignments[j.id] || assignments[j.id] !== user?.id);

  return (
    <RfShell title="Picking" back>
      <div className="text-xs text-muted-foreground px-1">
        {isLoading ? "Loading…" : `${allJobs.length} open`}
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-destructive">Could not load pick jobs</div>
            <div className="text-muted-foreground break-all">{(error as Error).message}</div>
          </div>
        </div>
      ) : null}

      {mine.length > 0 && (
        <div className="space-y-2">
          <div className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Assigned to me
          </div>
          {mine.map((j) => (<JobRow key={j.id} job={j} onStart={startJob} />))}
        </div>
      )}

      <div className="space-y-2">
        <div className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Available
        </div>
        {isLoading ? (
          <RfEmpty>Loading…</RfEmpty>
        ) : others.length === 0 ? (
          <RfEmpty>No open pick jobs for this facility.</RfEmpty>
        ) : (
          others.map((j) => <JobRow key={j.id} job={j} onStart={startJob} />)
        )}
      </div>

      <p className="px-1 pt-2 text-[11px] text-muted-foreground">
        Tap a job to start or resume its pick session.
      </p>
    </RfShell>
  );
}

function JobRow({ job, onStart }: { job: PickJobRow; onStart: (j: PickJobRow) => void }) {
  const typeLabel =
    job.type === "bulk_batch" ? "Bulk / Batch"
    : job.type === "pick_to_bin" ? "Pick to Cart"
    : "Order by Order";
  return (
    <button
      type="button"
      onClick={() => onStart(job)}
      className="w-full flex items-center gap-3 rounded-md border bg-card px-3 py-3 active:bg-muted/70 text-left"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-sm">{job.customer_name}</div>
        <div className="text-xs text-muted-foreground">
          {typeLabel} · {job.order_count} order{job.order_count === 1 ? "" : "s"}
        </div>
      </div>
      <StatusPill tone={statusTone(job.status)} dot>
        {job.status.replace("_", " ")}
      </StatusPill>
      {job.status === "in_progress" ? (
        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
      ) : (
        <PlayCircle className="h-5 w-5 text-primary shrink-0" />
      )}
    </button>
  );
}